import { useAuth } from '@/components/auth/authProvider';
import RequirePalmettoFinanceUser, { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import RequirePermissions from '@/components/auth/requirePermissions';
import { HoverOnlyTooltipComponent } from '@/components/Tooltip';
import { useEditOrganizationMutation, useGetOrganizationDetailsQuery } from '@/services/organizations';
import {
  Badge,
  Box,
  Button,
  Icon,
  SelectInputNative,
  Table,
  toast,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ProgramType, ProgramTypeLabels, StateLicenseNumber, StateLicenseStatus, UserPermissions } from 'types';

import { LicenseModal } from './LicenseModal';
import { noop, tooltipContainerStyle, tooltipTriggerStyle } from './Underwriting/constants';

const INHERITED_TOOLTIP = 'Inherited from the organization';

// Italics keep inherited values distinguishable from stored ones when the grey alone is too subtle.
const FALLBACK_VALUE_STYLE: React.CSSProperties = { fontStyle: 'italic' };
const LEGAL_ENTITY_CELL_STYLE: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '6px' };
// Fixed-width slot so values line up whether or not the row shows the inherited icon.
const ICON_SLOT_STYLE: React.CSSProperties = { display: 'inline-flex', width: '16px', justifyContent: 'center' };

const renderLegalEntityValue = (value: string | undefined, fallback: string | undefined) =>
  value ? (
    <div style={LEGAL_ENTITY_CELL_STYLE}>
      <span style={ICON_SLOT_STYLE} />
      <span>{value}</span>
    </div>
  ) : (
    <div style={LEGAL_ENTITY_CELL_STYLE}>
      <span style={ICON_SLOT_STYLE}>
        <HoverOnlyTooltipComponent
          content={INHERITED_TOOLTIP}
          onClose={noop}
          displayedContainerStyle={tooltipTriggerStyle}
          tooltipContainerStyle={tooltipContainerStyle}
          withPortal
        >
          <Icon name="c-info" size="sm" color="grey-500" aria-label={INHERITED_TOOLTIP} />
        </HoverOnlyTooltipComponent>
      </span>
      <Box as="span" display="inline" color="grey-500" data-fallback="true" style={FALLBACK_VALUE_STYLE}>
        {fallback}
      </Box>
    </div>
  );

const isSameLicense = (a: StateLicenseNumber, b?: StateLicenseNumber) =>
  b !== undefined && a.state === b.state && a.programType === b.programType;

interface LicenseRowActionsProps {
  license: StateLicenseNumber;
  rowIndex: number;
  onEdit: (license: StateLicenseNumber) => void;
  onDelete: (rowIndex: number) => void;
}

const LicenseRowActions = ({ license, rowIndex, onEdit, onDelete }: LicenseRowActionsProps) => {
  const handleEdit = useCallback(() => onEdit(license), [onEdit, license]);
  const handleDelete = useCallback(() => onDelete(rowIndex), [onDelete, rowIndex]);
  return (
    <Box direction="row" gap="xs">
      <Button
        variant="tertiary"
        size="sm"
        tone="neutral"
        onClick={handleEdit}
        iconPrefix="pencil"
        aria-label={`Edit ${license.state} license`}
      />
      <Button
        variant="tertiary"
        size="sm"
        tone="neutral"
        onClick={handleDelete}
        iconPrefix="trash"
        aria-label={`Delete ${license.state} license`}
      />
    </Box>
  );
};

export const OrganizationStateLicenses = () => {
  const [searchParams] = useSearchParams();
  const programType = searchParams.get('programType');
  const params = useParams<{ alias: string }>();
  const { claims } = useAuth();
  const isFincoUser = isPalmettoFinanceUser(claims);
  const { alias } = params;
  const navigate = useNavigate();
  const { data: organization } = useGetOrganizationDetailsQuery({ alias: alias as string });
  const [update] = useEditOrganizationMutation();
  const { isOpen, handleOpen, handleClose } = useOpenClose();
  const [licenseBeingEdited, setLicenseBeingEdited] = useState<StateLicenseNumber | undefined>();

  // Derive filtered licenses directly from organization data
  const filteredLicenses = useMemo(() => {
    if (!organization?.stateLicenseNumbers) return [];
    return organization.stateLicenseNumbers
      .filter((license) => license.programType === programType)
      .toSorted((a, b) => a.state.localeCompare(b.state));
  }, [organization?.stateLicenseNumbers, programType]);

  // Default to first program type if none selected
  useEffect(() => {
    const programTypes = organization?.programTypes;
    if (programTypes?.length && !programTypes.includes(programType as ProgramType)) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('programType', programTypes[0]);
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [organization?.programTypes, programType, searchParams, navigate]);

  const programTypesOptions = organization?.programTypes?.map((pt: ProgramType) => ({
    label: ProgramTypeLabels[pt],
    value: pt,
  }));

  const handleProgramTypeChange = (event: any) => {
    const newProgramType = event.target.value ?? '';
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('programType', newProgramType);
    navigate({ search: newSearchParams.toString() });
  };

  const handleDelete = async (rowIndex: number) => {
    const license = filteredLicenses[rowIndex];
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the license for ${license.state}? This action cannot be undone.`,
    );
    if (confirmDelete) {
      const otherProgramLicenses = organization?.stateLicenseNumbers?.filter((l) => l.programType !== programType);
      const updatedLicenses = filteredLicenses.filter((_, index) => index !== rowIndex);
      try {
        await update({
          organization: {
            licenses: [...updatedLicenses, ...(otherProgramLicenses || [])],
          },
          alias: alias as string,
        }).unwrap();
        toast.success('License deleted successfully.');
      } catch (error) {
        console.error(error);
        toast.error('Failed to delete license. Please try again.');
      }
    }
  };

  // license numbers are not unique across states, so key rows on state + program type.
  const tableRows = useMemo(
    () => filteredLicenses.map((license) => ({ ...license, rowKey: `${license.state}-${license.programType}` })),
    [filteredLicenses],
  );

  const otherProgramLicenses = useMemo(
    () => organization?.stateLicenseNumbers?.filter((license) => license.programType !== programType) ?? [],
    [organization?.stateLicenseNumbers, programType],
  );

  // Compare by identity fields, not reference: a refetch while the modal is open gives every license a new object.
  const takenStates = useMemo(
    () =>
      filteredLicenses
        .filter((license) => !isSameLicense(license, licenseBeingEdited))
        .map((license) => license.state),
    [filteredLicenses, licenseBeingEdited],
  );

  const handleAddLicense = useCallback(() => {
    setLicenseBeingEdited(undefined);
    handleOpen();
  }, [handleOpen]);

  const handleEditLicense = useCallback(
    (license: StateLicenseNumber) => {
      setLicenseBeingEdited(license);
      handleOpen();
    },
    [handleOpen],
  );

  const handleCloseModal = useCallback(() => {
    handleClose();
    setLicenseBeingEdited(undefined);
  }, [handleClose]);

  // Rejections propagate so the modal can show them and stay open.
  const handleSubmitLicense = useCallback(
    async (saved: StateLicenseNumber) => {
      const original = licenseBeingEdited;
      const currentLicenses = original
        ? filteredLicenses.map((license) => (isSameLicense(license, original) ? saved : license))
        : [...filteredLicenses, saved];

      await update({
        organization: { licenses: [...currentLicenses, ...otherProgramLicenses] },
        alias: alias as string,
      }).unwrap();
      toast.success('License saved successfully.');
      handleCloseModal();
    },
    [licenseBeingEdited, filteredLicenses, otherProgramLicenses, update, alias, handleCloseModal],
  );

  const stateLicenseColumns = [
    {
      heading: 'Status',
      dataKey: 'status',
      render: (cell?: unknown) => (
        <Badge
          variant={
            cell === StateLicenseStatus.active
              ? 'success'
              : cell === StateLicenseStatus.inactive
                ? 'warning'
                : 'danger'
          }
          message={cell as StateLicenseStatus}
        />
      ),
    },
    {
      heading: 'State',
      dataKey: 'state',
      render: (cell?: unknown) => (
        <Box fontWeight="medium" as="span" display="inline">
          {cell as string}
        </Box>
      ),
    },
    { heading: 'License Number', dataKey: 'contractorLicenseNumber' },
    { heading: 'Monetary Limit', dataKey: 'monetaryLimit' },
    { heading: 'Additional License Numbers', dataKey: 'additionalLicenseNumbers' },
    {
      heading: 'Legal Entity Name',
      dataKey: 'legalEntityName',
      render: (cell?: unknown) => renderLegalEntityValue(cell as string | undefined, organization?.name),
    },
    {
      heading: 'Legal Entity FEIN',
      dataKey: 'legalEntityFein',
      render: (cell?: unknown) =>
        renderLegalEntityValue(cell as string | undefined, organization?.federalEmployerId),
    },
    ...(isFincoUser
      ? [
          {
            heading: 'Actions',
            render: (_cell: unknown, _row: unknown, rowIndex?: number) => (
              <RequirePermissions permissions={[UserPermissions.admin]}>
                <LicenseRowActions
                  license={filteredLicenses[rowIndex ?? 0]}
                  rowIndex={rowIndex ?? 0}
                  onEdit={handleEditLicense}
                  onDelete={handleDelete}
                />
              </RequirePermissions>
            ),
          },
        ]
      : []),
  ];

  return (
    <Box margin="lg 0 0 0" childGap="md">
      <Box as="h4" fontSize="md" fontWeight="medium">
        State Licenses
      </Box>
      <Box direction="row" alignItems="center" justifyContent="space-between" gap="md">
        <Box direction="row" alignItems="center" gap="md">
          {organization?.programTypes?.length && organization?.programTypes?.length > 1 && (
            <Box maxWidth="2xl">
              <SelectInputNative
                id="required"
                label="Program Type"
                value={programType || null}
                onChange={handleProgramTypeChange}
                options={programTypesOptions || []}
                menuPortalTarget={document.body}
              />
            </Box>
          )}
        </Box>
        <RequirePalmettoFinanceUser>
          <RequirePermissions permissions={[UserPermissions.admin]}>
            <Button variant="primary" size="sm" iconPrefix="add" onClick={handleAddLicense}>
              Add License
            </Button>
          </RequirePermissions>
        </RequirePalmettoFinanceUser>
      </Box>
      {filteredLicenses.length ? (
        <Table
          key={programType}
          isCompact
          rowKey="rowKey"
          columns={stateLicenseColumns}
          rows={tableRows}
          isScrollable={{
            x: true,
          }}
        />
      ) : (
        'No licenses on file'
      )}
      {isOpen && (
        <LicenseModal
          isOpen={isOpen}
          onClose={handleCloseModal}
          programType={programType as ProgramType}
          license={licenseBeingEdited}
          takenStates={takenStates}
          onSubmit={handleSubmitLicense}
        />
      )}
    </Box>
  );
};
